import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getUserSubscription, 
  hasFeatureAccess, 
  getSubscriptionDisplayInfo,
  PREMIUM_FEATURES,
  subscribeToSubscription
} from '../services/subscriptionService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children, user }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load subscription data when user changes
  useEffect(() => {
    if (!user?.uid) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const unsubscribe = subscribeToSubscription(
      user.uid,
      (subscriptionData) => {
        setSubscription(subscriptionData);
        setLoading(false);
        console.log('Loaded subscription data via listener:', subscriptionData);
      },
      (err) => {
        console.error('Error loading subscription:', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

// Refresh subscription data
  const refreshSubscription = async () => {
    if (!user?.uid) return;
    
    try {
      const subscriptionData = await getUserSubscription(user.uid);
      setSubscription(subscriptionData);
      return subscriptionData;
    } catch (err) {
      console.error('Error refreshing subscription:', err);
      setError(err.message);
      throw err;
    }
  };

  // Check feature access
  const checkFeatureAccess = (feature) => {
    return hasFeatureAccess(subscription, feature);
  };

  // Get display information
  const getDisplayInfo = () => {
    return getSubscriptionDisplayInfo(subscription);
  };

  // Feature access helpers
  const canUseVoiceToText = () => checkFeatureAccess(PREMIUM_FEATURES.VOICE_TO_TEXT);
  const canUseReceiptScanner = () => checkFeatureAccess(PREMIUM_FEATURES.RECEIPT_SCANNER);
  const canUseAdvancedReports = () => checkFeatureAccess(PREMIUM_FEATURES.ADVANCED_REPORTS);

  const value = {
    // State
    subscription,
    loading,
    error,
    
    // Actions
    refreshSubscription,
    
    // Feature checks
    checkFeatureAccess,
    canUseVoiceToText,
    canUseReceiptScanner,
    canUseAdvancedReports,
    
    // Display info
    getDisplayInfo,
    
    // Direct access to subscription info
    isPremium: subscription?.subscriptionPlan === 'premium',
    isTrialActive: subscription?.subscriptionStatus === 'trial',
    isFree: subscription?.subscriptionPlan === 'free' || !subscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
